import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/**
 * Browser Supabase client boundary — AUTH ONLY.
 *
 * This module exists solely to establish and maintain the user's Supabase Auth
 * session: sign in, sign up, sign out, session refresh, and auth-state changes.
 *
 * Per ADR-0006, browser app code must not read or write app data directly through
 * Supabase. App data flows through Worker `/api/*` routes using the user's
 * session access token as `Authorization: Bearer <token>`.
 *
 * The full Supabase client is intentionally kept private in this module. Exports
 * expose only the `.auth` surface so browser code cannot call `.from()`, `.rpc()`,
 * or `.storage()` through this helper.
 *
 * This module reads exactly two public build-time values:
 * - VITE_SUPABASE_URL
 * - VITE_SUPABASE_ANON_KEY
 *
 * There is deliberately no service-role name here. Service-role secrets are
 * server-only and must never be exposed through VITE_* variables.
 */
export interface SupabaseAuthConfig {
  url: string;
  anonKey: string;
}

export type SupabaseAuthOnlyClient = Pick<SupabaseClient, 'auth'>;

export function readSupabaseAuthConfig(env: ImportMetaEnv): SupabaseAuthConfig {
  const url = env.VITE_SUPABASE_URL;
  const anonKey = env.VITE_SUPABASE_ANON_KEY;

  if (!url || !anonKey) {
    throw new Error(
      'Supabase Auth is not configured: set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.',
    );
  }

  return { url, anonKey };
}

export function createAuthClient(config: SupabaseAuthConfig): SupabaseAuthOnlyClient {
  const client = createClient(config.url, config.anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });

  return { auth: client.auth };
}

let cachedClient: SupabaseAuthOnlyClient | null = null;

export function getSupabaseAuthClient(): SupabaseAuthOnlyClient {
  if (!cachedClient) {
    cachedClient = createAuthClient(readSupabaseAuthConfig(import.meta.env));
  }

  return cachedClient;
}
